/**
 * A CALLED TEST ON THE WIRE - what the person asked sees, who it is sent
 * to, and where a secret result goes.
 *
 * The prompt a player receives says who is asked and for what, in words;
 * it never carries the opposition's numbers or anything the GM has not
 * said aloud.
 *
 * An ORDINARY prompt carries the call itself (v9.5.3), cut to the one
 * person asked, so the asked screen rolls it with its own dice and sends
 * the result back as the ask's answer (`testAnswer.ts`); the GM's screen
 * accepts it and writes the line. A SECRET prompt carries no call: pressing
 * Roll is a SIGNAL, the dice are rolled on the GM's screen, so a secret
 * result never exists on the player's machine at all (anything a client
 * rolls, its console can read - and could forge, which is the accepted
 * price of an ordinary test landing where the player is).
 *
 * A secret result is written as a line of the vox addressed to the GM
 * alone - the one audience the rules guarantee nobody else can read.
 *
 * Pure, zero React dependency.
 */
import { DIFFICULTY_LADDER } from '../characteristicTests';
import type { ChatMessage } from '../chat/types';
import { readTestCall, subjectLabel } from './callForTest';
import { clampAssists } from './assist';
import type { TestCall } from './callForTest';

export interface TestAsk {
  /** The call this prompt belongs to, on the GM's screen. */
  callId: string;
  combatantId: string;
  /** Who is asked - the combatant's name. */
  name: string;
  /** What for, in words: "Scrutiny (Hard -20)", "Scrutiny against Deceive". */
  label: string;
  secret: boolean;
  /** The call, for the asked screen to roll: `who` is this one person. ABSENT on a secret call. */
  call?: TestCall;
}

const signed = (n: number) => (n >= 0 ? `+${n}` : String(n));

/** "Hard -20", or the bare number when it is not a step of the ladder. */
function difficultyText(mod: number): string {
  const step = DIFFICULTY_LADDER.find((s) => s.mod === mod);
  return step ? `${step.label} ${signed(mod)}` : signed(mod);
}

/** What a call asks for, in words. An opposed call names both subjects and nobody's numbers. */
export function callLabel(call: TestCall): string {
  const subject = subjectLabel(call.subject);
  const helped = clampAssists(call.assists);
  const withHelp = helped ? `, ${helped} assisting` : '';
  if (call.against) return `${subject} against ${subjectLabel(call.against.subject)}${withHelp}`;
  return `${subject} (${difficultyText(call.difficulty)})${withHelp}`;
}

/**
 * The call as the asked screen may have it, or null when it may not have it at all.
 *
 * `asks` is read by every member. A SECRET call and an OPPOSED one stay with
 * the GM (the result, and who it is against at what difficulty, are not said
 * aloud): the prompt carries no call, Roll is a signal, and the GM's screen
 * rolls. An ordinary call goes without its riders and its prompt list - a
 * kept board entry's id, the clock it fills, the accounts asked - which are
 * applied where the call is kept (the review, 2026-10-06).
 */
function callForAsked(call: TestCall, combatantId: string): TestCall | null {
  if (call.secret || call.against) return null;
  const { who: _all, subject, difficulty, against, secret, assists } = call;
  return { who: [combatantId], subject, difficulty, against, secret, ...(assists === undefined ? {} : { assists }) };
}

export function testAskOf(callId: string, combatant: { id: string; name: string }, call: TestCall): TestAsk {
  const carried = callForAsked(call, combatant.id);
  return {
    callId,
    combatantId: combatant.id,
    name: combatant.name,
    label: callLabel(call),
    secret: call.secret,
    ...(carried ? { call: carried } : {}),
  };
}

const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/** A prompt that crossed the wire, validated; null when it does not say who is asked or for which call. */
export function readTestAsk(raw: unknown): TestAsk | null {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const callId = text(stored.callId);
  const combatantId = text(stored.combatantId);
  if (!callId || !combatantId) return null;
  const call = stored.call === undefined ? null : readTestCall(stored.call);
  return {
    callId,
    combatantId,
    name: text(stored.name) ?? 'Someone',
    label: text(stored.label) ?? 'a test',
    secret: stored.secret === true,
    ...(call ? { call } : {}),
  };
}

/**
 * The account playing this combatant, from the seats (`actingBy` is account
 * to combatant) - never `exceptUid`, the GM's own: a combatant the GM acts
 * as is rolled at once, not asked for.
 */
export function playerFor(
  combatantId: string,
  actingBy: Readonly<Record<string, string>>,
  exceptUid: string | null,
): string | null {
  const found = Object.entries(actingBy).find(([uid, acting]) => acting === combatantId && uid !== exceptUid);
  return found ? found[0] : null;
}

interface Writer {
  uid: string;
  seat: string;
  name: string;
}

/** A secret result, as a line only its writer can read: addressed to them alone, in their own conversation. */
export function secretNote(me: Writer, body: string, ts: number, id: string): ChatMessage {
  return {
    id,
    ts,
    fromUid: me.uid,
    fromSeat: me.seat,
    fromName: me.name,
    speakAs: null,
    kind: 'say',
    text: `SECRET - ${body}`,
    conv: `dm:${me.uid}`,
    audience: [me.uid],
    mentions: [],
    roll: null,
    handoutId: '',
  };
}
